// src/pages/HelpPage.tsx
//
// ★ PHASE 10 — Annex A stories 24 ("View Online Help") and 25 ("Download User
// Manual"). One screen serves both: a searchable topic list (story 24), and a
// download button for the same content rendered as a PDF (story 25) — see
// `help-content.ts`'s own header for why there is only one catalogue.
import { useState } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate } from '../lib/format'

export function HelpPage() {
  const [query, setQuery] = useState('')
  const [openTopicId, setOpenTopicId] = useState<string | null>(null)

  const topics = useApi(() => api.helpTopics(query), [query])
  const detail = useApi(
    () => (openTopicId === null ? Promise.resolve(null) : api.helpTopic(openTopicId)),
    [openTopicId],
  )

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-2">Help &amp; user manual</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Search the topics below, or download the whole manual for offline
            reference.
          </p>
        </div>
        {topics.data !== undefined && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[var(--sgds-body-color-subtle)]">
              v{topics.data.manualVersion} &middot; updated {formatIsoDate(topics.data.manualUpdatedOn)}
            </span>
            <SgdsButton variant="outline" type="button" onClick={() => (window.location.href = api.helpManualUrl('pdf'))}>
              Download PDF
            </SgdsButton>
            <SgdsButton variant="outline" type="button" onClick={() => (window.location.href = api.helpManualUrl('xlsx'))}>
              Download Excel
            </SgdsButton>
          </div>
        )}
      </div>

      <SgdsInput
        label="Search help topics"
        value={query}
        onSgdsInput={(e) => setQuery(targetValue(e))}
        placeholder="e.g. approval, entitlement, estimate"
      />

      {topics.error !== null ? (
        <ErrorCard title="Could not load help topics" message={topics.error} onRetry={topics.reload} />
      ) : topics.initialLoading ? (
        <LoadingCard label="Loading help topics" rows={4} />
      ) : (topics.data?.topics.length ?? 0) === 0 ? (
        <EmptyCard title="No matching topics" message="Try a different search term, or clear the search box." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {topics.data?.topics.map((topic) => (
            <button
              key={topic.id}
              type="button"
              className="pt-card text-left cursor-pointer"
              onClick={() => setOpenTopicId(topic.id)}
              aria-expanded={openTopicId === topic.id}
            >
              <p className="text-xs uppercase tracking-wide text-[var(--sgds-body-color-subtle)] mb-1">
                {topic.category}
              </p>
              <p className="font-semibold mb-1">{topic.title}</p>
              <p className="text-sm text-[var(--sgds-body-color-subtle)]">{topic.summary}</p>
            </button>
          ))}
        </div>
      )}

      {openTopicId !== null && (
        <div className="pt-card">
          {detail.error !== null ? (
            <ErrorCard title="Could not open this topic" message={detail.error} />
          ) : detail.initialLoading ? (
            <LoadingCard label="Loading topic" rows={3} />
          ) : detail.data !== undefined && detail.data !== null ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-xl">{detail.data.title}</h2>
                <SgdsButton variant="ghost" size="sm" type="button" onClick={() => setOpenTopicId(null)}>
                  Close
                </SgdsButton>
              </div>
              {detail.data.body.map((paragraph, index) => (
                <p key={index} className="max-w-[75ch]">
                  {paragraph}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
